<script>
import Vue from "vue";

function mediaName(media) {
    if (!media) return "";
    if (media.name) return media.name;
    if (media.path && typeof media.path === "string" && !media.path.startsWith("data:")) {
        return media.path.split("/").pop();
    }
    return "";
}

function mediaPreviewUrl(media) {
    if (!media) return "";
    if (typeof media.path === "string" && media.path.startsWith("data:")) return media.path;
    if (media.preSignedUrl) return media.preSignedUrl;
    if (media.presignedUrl) return media.presignedUrl;
    return "";
}

export default {
    name: "StoreCroppedFilesField",
    props: ["propertyName", "value", "object", "options"],

    data() {
        return {
            mode: Array.isArray(this.value) && this.value.length > 0 ? "sourceAndCrops" : "sourceOnly",
            vlboxes: [],
            loading: false,
            error: null
        };
    },

    computed: {
        currentFiles() {
            return Array.isArray(this.value) ? this.value : [];
        },
        buildingId() {
            return this.object?.buildingId || null;
        },
        isSourceAndCrops() {
            return this.mode === "sourceAndCrops";
        },
        canUseCrops() {
            return this.vlboxes.length > 0;
        }
    },

    watch: {
        buildingId: {
            immediate: true,
            handler() {
                this.loadVlboxes();
            }
        },
        value() {
            if (Array.isArray(this.value) && this.value.length > 0) {
                this.mode = "sourceAndCrops";
            }
        }
    },

    methods: {
        async loadVlboxes() {
            this.vlboxes = [];
            this.error = null;
            if (!this.buildingId) return;

            this.loading = true;
            try {
                const devices = await Vue.backoffice.requestor.get("/vlbox", null, {
                    buildingId: this.buildingId,
                    type: "vlbox"
                });
                this.vlboxes = (Array.isArray(devices) ? devices : [])
                    .filter(device => device.type === "vlbox" && device.mode !== "light")
                    .sort((a, b) => (a.name || "").localeCompare(b.name || "", undefined, { numeric: true }));
                if (this.vlboxes.length === 0) {
                    this.mode = "sourceOnly";
                    this.$emit("input", []);
                }
                this.ensureRows();
            } catch (err) {
                console.error("Unable to load building VLBoxes", err);
                this.error = "Impossible de charger les VLBox du bâtiment.";
            } finally {
                this.loading = false;
            }
        },

        setMode(mode) {
            this.mode = mode;
            if (mode === "sourceOnly") {
                this.$emit("input", []);
            } else {
                this.ensureRows();
            }
        },

        ensureRows() {
            if (!this.isSourceAndCrops) return;
            const nextFiles = this.vlboxes.map(box => {
                return this.currentFiles.find(file => file.vlboxId?.toString?.() === box._id?.toString?.())
                    || { vlboxId: box._id, video: null };
            });
            this.$emit("input", nextFiles);
        },

        openInput(vlboxId) {
            const input = this.$refs[`file-${vlboxId}`];
            const el = Array.isArray(input) ? input[0] : input;
            if (el) el.click();
        },

        readFile(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.addEventListener("load", () => resolve(reader.result));
                reader.addEventListener("error", reject);
                reader.readAsDataURL(file);
            });
        },

        async fileChanged(vlboxId, event) {
            const file = event.target.files && event.target.files[0];
            if (!file) return;

            const dataUri = await this.readFile(file);
            const nextFiles = this.vlboxes.map(box => {
                const existing = this.currentFiles.find(item => item.vlboxId?.toString?.() === box._id?.toString?.());
                if (box._id.toString() !== vlboxId.toString()) {
                    return existing || { vlboxId: box._id, video: null };
                }
                return {
                    vlboxId: box._id,
                    video: {
                        path: dataUri,
                        mimetype: file.type || "video/mp4",
                        name: file.name
                    }
                };
            });
            this.$emit("input", nextFiles);
            event.target.value = "";
        },

        clearFile(vlboxId) {
            this.$emit("input", this.currentFiles.map(file => {
                if (file.vlboxId?.toString?.() !== vlboxId.toString()) return file;
                return { vlboxId, video: null };
            }));
        },

        fileFor(vlboxId) {
            return this.currentFiles.find(file => file.vlboxId?.toString?.() === vlboxId?.toString?.());
        },

        mediaName,
        mediaPreviewUrl,
        isImageMedia(media) {
            return (media?.mimetype || "").startsWith("image/");
        }
    }
};
</script>

<template>
    <div class="StoreCroppedFilesField">
        <div v-if="canUseCrops" class="mode-switch">
            <button type="button" :class="{ active: mode === 'sourceOnly' }" @click="setMode('sourceOnly')">
                Source seule
            </button>
            <button type="button" :class="{ active: mode === 'sourceAndCrops' }" @click="setMode('sourceAndCrops')">
                Source + fichiers découpés
            </button>
        </div>

        <div v-if="isSourceAndCrops && canUseCrops" class="vlbox-files">
            <div v-if="!buildingId" class="empty">Sélectionnez un bâtiment pour charger ses VLBox.</div>
            <div v-else-if="loading" class="empty">Chargement des VLBox...</div>
            <div v-else-if="error" class="error">{{ error }}</div>
            <div v-else-if="vlboxes.length === 0" class="empty">Aucune VLBox vidéo ou audio trouvée.</div>
            <div v-else v-for="box in vlboxes" :key="box._id" class="vlbox-row">
                <div class="vlbox-name">
                    <strong>{{ box.name }}</strong>
                    <span>{{ box.mode }}</span>
                </div>
                <div class="file-cell">
                    <button type="button" class="Button LittleButton Shy" @click="openInput(box._id)">
                        Choisir un fichier
                    </button>
                    <input
                        :ref="'file-' + box._id"
                        type="file"
                        accept="video/*,image/*"
                        @change="fileChanged(box._id, $event)"
                    />
                    <div v-if="fileFor(box._id) && fileFor(box._id).video" class="selected-file">
                        <span>{{ mediaName(fileFor(box._id).video) || "Fichier sélectionné" }}</span>
                        <button type="button" @click="clearFile(box._id)">supprimer</button>
                    </div>
                    <img
                        v-if="fileFor(box._id) && mediaPreviewUrl(fileFor(box._id).video) && isImageMedia(fileFor(box._id).video)"
                        :src="mediaPreviewUrl(fileFor(box._id).video)"
                        alt=""
                    />
                    <video
                        v-else-if="fileFor(box._id) && mediaPreviewUrl(fileFor(box._id).video)"
                        :src="mediaPreviewUrl(fileFor(box._id).video)"
                        controls
                        playsinline
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
@import "../../../packages/@snark/backoffice/src/sass/variables";

.StoreCroppedFilesField {
    .mode-switch {
        display: inline-flex;
        border: $buttonShyBorder solid 1px;
        border-radius: 4px;
        overflow: hidden;
        margin-bottom: $gap;

        button {
            border: 0;
            background: $mainBackground;
            color: $title2Color;
            padding: 10px 14px;
            cursor: pointer;
            font-weight: $regular;

            &.active {
                background: $buttonBackground;
                color: $buttonText;
            }
        }
    }

    .vlbox-files {
        display: flex;
        flex-direction: column;
        gap: $gap2;
    }

    .vlbox-row {
        display: flex;
        gap: $gap;
        align-items: flex-start;
        padding: $gap2;
        background: $mainBackground;
        border-radius: 6px;
    }

    .vlbox-name {
        flex: 0 0 220px;
        display: flex;
        flex-direction: column;
        gap: 4px;

        span {
            color: $textColor;
            font-size: 12px;
        }
    }

    .file-cell {
        flex: 1 1 0;

        input {
            display: none;
        }

        video,
        img {
            display: block;
            width: 100%;
            max-width: 420px;
            max-height: 240px;
            margin-top: $gap3;
            background: #000;
            border-radius: 4px;
            object-fit: contain;
        }
    }

    .selected-file {
        display: flex;
        align-items: center;
        gap: $gap2;
        margin-top: $gap3;
        color: $title2Color;

        button {
            border: 0;
            color: $error;
            background: transparent;
            cursor: pointer;
        }
    }

    .empty,
    .error {
        color: $textColor;
        padding: $gap2;
        background: $mainBackground;
        border-radius: 4px;
    }

    .error {
        color: $error;
    }
}
</style>
